import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Flame } from "lucide-react";
import api from "../lib/api";
import { catMeta, CATEGORY_META } from "../lib/verdictly";
import VerdictResult from "../components/VerdictResult";

const login = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const CTA = {
  roast: { label: "Roast Me", emoji: "🔥" },
  judge: { label: "Judge Me", emoji: "👀" },
  validate: { label: "Validate My Idea", emoji: "🚀" },
};

export default function ResultPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try { setData((await api.get(`/results/${token}`)).data); }
      catch (e) { setErr(true); }
    })();
  }, [token]);

  if (err) return <Shell><div className="text-center text-zinc-400 py-20" data-testid="result-notfound">This result isn't available.</div></Shell>;
  if (!data) return <Shell><div className="text-center text-zinc-400 py-20">Loading…</div></Shell>;

  const m = catMeta(data.category);
  const pending = data.status !== "complete";

  return (
    <Shell>
      <div className="max-w-xl mx-auto" data-testid="public-result">
        <div className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-4" style={{ color: m.accent, background: m.soft }}>
          {data.category_emoji} {data.category_name}{data.is_retest && " · Retest"}
        </div>
        <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl mb-6">The verdict is in</h1>

        {data.content_type === "text" ? (
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-5 text-zinc-200 mb-6">{data.content}</div>
        ) : (
          <img src={data.content} alt="" className="rounded-xl border border-white/10 max-h-72 mb-6" />
        )}

        {pending ? (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-8 text-center text-zinc-400" data-testid="public-pending">
            The panel is still weighing in ({data.reviews_count}/{data.reviewers_required}). Check back shortly.
          </div>
        ) : (
          <VerdictResult task={data} result={data.result} before={data.before_headline} testPrefix="public" />
        )}

        {/* "Your Turn" viral close */}
        <div className="mt-12 pt-8 border-t border-white/10" data-testid="your-turn">
          <div className="text-center mb-5">
            <div className="font-heading font-bold uppercase tracking-tighter text-2xl">Your turn</div>
            <p className="text-zinc-400 text-sm mt-1">Ask 5 strangers. Get the truth.</p>
          </div>
          <div className="grid gap-3">
            {Object.entries(CTA).map(([key, c]) => {
              const cm = CATEGORY_META[key];
              return (
                <button key={key} data-testid={`yourturn-${key}`} onClick={login}
                  className="flex items-center justify-between rounded-xl border p-4 transition-all hover:-translate-y-0.5"
                  style={{ borderColor: cm.accent + "44", background: cm.soft }}>
                  <span className="font-heading font-bold text-lg">{c.emoji} {c.label}</span>
                  <span className="text-sm font-bold" style={{ color: cm.accent }}>Start →</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen grain relative overflow-hidden">
      <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-brand/20 blur-[120px] pointer-events-none" />
      <header className="relative z-10 max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2"><Flame className="text-brand" size={24} /><span className="font-heading font-bold text-xl tracking-tighter">RoastLit</span></a>
        <button data-testid="public-login-btn" onClick={login} className="px-4 py-2 rounded-full text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">Sign in</button>
      </header>
      <main className="relative z-10 max-w-6xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
}
